<template>
    <FilterBase :title="title">
        <InputRangeSegmented
            v-model="model"
            :segments-config
            :min="extractRangeInputValue(bounds, 'min')"
            :max="extractRangeInputValue(bounds, 'max')"
            :step="0.05"
            :value-formatter
            :debounce="500"
            :min-label
            :max-label
        />
    </FilterBase>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type {
    NonSkuCaratWeightRangeFragment,
    NonSkuCaratWeightRangeInput,
} from '@/graphql/api'

import FilterBase from '@/components/ui/filter/filters/layout/FilterBase.vue'
import type {
    InputRangeModel,
    InputRangeTwoKnobbedValueFormatter,
} from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import InputRangeSegmented from '@/components/ui/inputs/range/InputRangeSegmented.vue'
import { isUndefinedOrNull } from '@/helpers'
import type { SegmentConfig } from '@/components/ui/inputs/range/input-range-segmented'

interface Props {
    modelValue?: NonSkuCaratWeightRangeInput | null | undefined
    bounds: NonSkuCaratWeightRangeFragment
    step?: number
    title?: string
    valueFormatter?: InputRangeTwoKnobbedValueFormatter
    minLabel: string
    maxLabel: string
    segmentsConfig: SegmentConfig
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    step: 1,
    valueFormatter: undefined,
    title: undefined,
})

const emit = defineEmits<{
    'update:modelValue': [input: NonSkuCaratWeightRangeInput]
}>()

const model = computed({
    get: (): InputRangeModel => [
        getValueOrBound('min'),
        getValueOrBound('max'),
    ],
    set: (v: InputRangeModel) => {
        emit('update:modelValue', {
            min: isUndefinedOrNull(v[0]) ? v[0] : String(v[0]),
            max: isUndefinedOrNull(v[1]) ? v[1] : String(v[1]),
        })
    },
})

function getValueOrBound(key: keyof RangeInput): number | undefined {
    return extractRangeInputValue(props.modelValue || props.bounds, key)
}

type RangeInput = Pick<NonSkuCaratWeightRangeInput, 'min' | 'max'>

function extractRangeInputValue(
    value: RangeInput,
    key: keyof RangeInput
): number | undefined {
    const val = value[key]

    if (isUndefinedOrNull(val)) {
        return undefined
    }

    const asNumber = Number(val)

    if (isNaN(asNumber)) {
        return undefined
    }

    return asNumber
}
</script>
